import { SearchOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from '@tanstack/react-router';
import PageContent from '@zeus/core/layout/PageContent';
import { FormattedIntegration } from '_/api/integrations/integrations.api';
import AsyncPageContainer from '_/common/components/layout/AsyncPageContainer';
import { useAlarmGroupContext } from '_/contexts/AlarmGroupContext';
import { useFlowContext } from '_/contexts/FlowContext';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import {
  useAlarmGroupIntegrations,
  useLinkIntegrationToAlarmGroup,
  useUnlinkIntegrationFromAlarmGroup,
} from '_/hooks/useAlarmGroupIntegrations';
import { useIntegrations } from '_/hooks/useIntegrations';
import useOrgAccess from '_/hooks/useOrgAccess';
import { SOURCE_ENGINEER_NOT_INCLUDED } from '_/utils/userManagement';
import { Table, Checkbox, Tooltip, Switch, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { useState, ReactElement, useMemo, useEffect } from 'react';

interface LinkedIntegration {
  id: string;
  alarmGroupIntegrationId: string;
  integrationId: string;
  name: string;
}

function AlarmGroupIntegrations(): ReactElement {
  const access = useOrgAccess(SOURCE_ENGINEER_NOT_INCLUDED);
  const history = useNavigate();
  const params = useParams({ strict: false }) as Partial<{
    orgSlug: string;
    projectSlug: string;
    flowSlug: string;
  }>;
  const orgSlug = params.orgSlug || '';
  const projectSlug = params.projectSlug || '';
  const flowSlug = params.flowSlug || '';
  const { selectedAlarmGroup, alarmGroupId } = useAlarmGroupContext();
  const { selectedFlow } = useFlowContext();
  const { selectedOrganization } = useOrganization();
  const { selectedProject } = useProject();
  const { setBreadcrumbs } = useTopHeader();

  // Fetch all org integrations
  const { data: allIntegrations = [], isLoading: isLoadingIntegrations } = useIntegrations(orgSlug);

  // Fetch alarm group integrations (linked ones)
  const { data: alarmGroupIntegrations = [], isLoading: isLoadingAlarmGroupIntegrations } =
    useAlarmGroupIntegrations(alarmGroupId);

  // Mutations
  const linkMutation = useLinkIntegrationToAlarmGroup(alarmGroupId);
  const unlinkMutation = useUnlinkIntegrationFromAlarmGroup(alarmGroupId);

  const [editMode, setEditMode] = useState(false);
  const [searchText, setSearchText] = useState('');

  // Transform alarm group integrations to include integration details
  const linkedAlarmGroupIntegrations = useMemo(() => {
    return (alarmGroupIntegrations as any[]).map((integration) => {
      return {
        id: integration.id, // This is the integration ID
        alarmGroupIntegrationId: integration.alarmGroupIntegration?.id || integration.id, // The linking ID
        integrationId: integration.id,
        name: integration.name || 'Unknown',
      };
    });
  }, [alarmGroupIntegrations]);

  const handleLinkIntegrationToAlarmGroup = async (integrationId: string) => {
    if (selectedAlarmGroup) {
      await linkMutation.mutateAsync({
        groupId: selectedAlarmGroup.id,
        integrationId: integrationId,
      });
    }
  };

  const handleConfirmUnlinkAlarmGroupFromIntegration = async (integrationId: string) => {
    const integration = linkedAlarmGroupIntegrations.find((i) => i.id === integrationId);

    if (integration) {
      await unlinkMutation.mutateAsync(integration.alarmGroupIntegrationId);
    }
  };

  const toggleEditMode = () => {
    const edited = !editMode;
    setEditMode(edited);
  };

  const checkAlarmGroupIntegration = (checked: boolean, integration: FormattedIntegration | LinkedIntegration) => {
    if (checked) {
      handleLinkIntegrationToAlarmGroup(integration?.id);
    } else {
      handleConfirmUnlinkAlarmGroupFromIntegration(integration?.id);
    }
  };

  const isLinked = (integrationId: string) => {
    return linkedAlarmGroupIntegrations.some((i) => i.id === integrationId);
  };

  const handleIntegrationClick = (integrationId: string) => {
    void history({ to: `/orgs/${orgSlug}/integrations/${integrationId}/edit` });
  };

  // Filter linked integrations based on search text
  const filteredLinkedIntegrations = linkedAlarmGroupIntegrations.filter((integration) => {
    if (!searchText) return true;
    const lowerSearch = searchText.toLowerCase();
    return integration.name?.toLowerCase().includes(lowerSearch) || integration.id?.toLowerCase().includes(lowerSearch);
  });

  // Filter all integrations based on search text
  const filteredIntegrations = allIntegrations.filter((integration) => {
    if (!searchText) return true;
    const lowerSearch = searchText.toLowerCase();
    return integration.name?.toLowerCase().includes(lowerSearch) || integration.id?.toLowerCase().includes(lowerSearch);
  });

  const linkedColumns: ColumnsType<LinkedIntegration> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text: string, record: LinkedIntegration) => (
        <a
          onClick={() => access && handleIntegrationClick(record.integrationId)}
          style={{ cursor: access ? 'pointer' : 'default', color: access ? undefined : 'inherit' }}
        >
          {text}
        </a>
      ),
    },
    {
      title: 'Linked',
      dataIndex: 'id',
      key: 'linked',
      width: 120,
      align: 'center',
      render: (_: string, record: LinkedIntegration) => (
        <Checkbox checked={true} onChange={(e) => checkAlarmGroupIntegration(e.target.checked, record)} disabled={!editMode} />
      ),
    },
  ];

  const unlinkedColumns: ColumnsType<FormattedIntegration> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text: string, record: FormattedIntegration) => (
        <a onClick={() => handleIntegrationClick(record.id)} style={{ cursor: 'pointer' }}>
          {text}
        </a>
      ),
    },
    {
      title: 'Linked',
      dataIndex: 'id',
      key: 'linked',
      width: 120,
      align: 'center',
      render: (id: string, record: FormattedIntegration) => (
        <Checkbox
          checked={isLinked(id)}
          onChange={(e) => checkAlarmGroupIntegration(e.target.checked, record)}
          disabled={!editMode}
        />
      ),
    },
  ];

  const defaultContent =
    linkedAlarmGroupIntegrations?.length > 0 ? (
      <Table
        columns={linkedColumns}
        dataSource={filteredLinkedIntegrations}
        rowKey="id"
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        }}
      />
    ) : (
      <p>No Linked Integrations</p>
    );

  const isLoading = isLoadingIntegrations || isLoadingAlarmGroupIntegrations;

  // Set up breadcrumbs
  useEffect(() => {
    setBreadcrumbs([
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${orgSlug}` },
      { title: 'Projects', href: `/orgs/${orgSlug}/projects` },
      { title: selectedProject?.name || '', href: `/orgs/${orgSlug}/projects/${projectSlug}/home` },
      { title: 'Flows', href: `/orgs/${orgSlug}/projects/${projectSlug}/flows` },
      {
        title: selectedFlow?.name || 'Flow',
        href: `/orgs/${orgSlug}/projects/${projectSlug}/flows/${flowSlug}/home`,
      },
      {
        title: 'Alarm Groups',
        href: `/orgs/${orgSlug}/projects/${projectSlug}/flows/${flowSlug}/alarmgroups`,
      },
      {
        title: selectedAlarmGroup?.name || 'Alarm Group',
        href: `/orgs/${orgSlug}/projects/${projectSlug}/flows/${flowSlug}/alarmgroups/${alarmGroupId}/home`,
      },
      { title: 'Integrations' },
    ]);

    return () => {
      setBreadcrumbs([]);
    };
  }, [
    setBreadcrumbs,
    selectedOrganization?.name,
    orgSlug,
    selectedProject?.name,
    projectSlug,
    selectedFlow?.name,
    flowSlug,
    selectedAlarmGroup?.name,
    alarmGroupId,
  ]);

  return (
    <AsyncPageContainer isLoading={isLoading}>
      <PageContent>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          {editMode || linkedAlarmGroupIntegrations?.length > 0 ? (
            <Input
              placeholder="Search integrations..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ maxWidth: 400 }}
              allowClear
            />
          ) : (
            <div />
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tooltip
              title={
                access ? 'Link/Unlink An Integration' : 'You do not have the right permissions to link/unlink an integration'
              }
            >
              <span>
                <Switch checked={editMode} onChange={toggleEditMode} disabled={!access} />
              </span>
            </Tooltip>
            <span>Link/Unlink</span>
          </div>
        </div>
        {editMode ? (
          <>
            <Table
              columns={unlinkedColumns}
              dataSource={filteredIntegrations}
              rowKey="id"
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              }}
            />
          </>
        ) : (
          defaultContent
        )}
      </PageContent>
    </AsyncPageContainer>
  );
}

export default AlarmGroupIntegrations;
